import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectedFilter } from '../components/Filters/types';

interface TablePaginationFiltersState {
    selectedFilters: SelectedFilter[];
    hideFilter: boolean;
    beforeFilters: SelectedFilter[] | null;
    wasEmptyBeforeFilter: boolean;
    filterByCurrentUser?: boolean;
    filterByOnlyActiveRecords?: boolean;
}

export type FilterTableKey =
    | 'dashboard'
    | 'users'
    | 'claims'
    | 'participants'
    | 'campaigns'
    | 'alerts'
    | 'adminSurveys'
    | 'roles'
    | 'auditLogs'
    | 'improvementPlans'
    | 'surveyLoads'
    | 'detailSurveyLoads'
    | 'generic'
    | 'ips'
    | 'notifications'
    | 'improvementPlanDemandsModal'
    | 'claimRequestTypes'
    | 'claimsForPersonByAffectedPeople'
    | 'claimsForPersonByClaimantClaims'
    | 'userCommunicationTypes'
    | 'professionals'
    | 'reportDefinitions';

type FiltersState = {
    [TableKey in FilterTableKey]: TablePaginationFiltersState;
};

const initialState: FiltersState = {
    dashboard: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true },
    claims: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByCurrentUser: false },
    participants: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true },
    campaigns: { selectedFilters: [], hideFilter: true, beforeFilters: null, wasEmptyBeforeFilter: true },
    alerts: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByCurrentUser: false },
    adminSurveys: { selectedFilters: [], hideFilter: true, beforeFilters: null, wasEmptyBeforeFilter: true },
    auditLogs: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true },
    roles: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    improvementPlans: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    surveyLoads: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    detailSurveyLoads: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    ips: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    users: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    generic: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true},
    notifications: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: false},
    improvementPlanDemandsModal: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: false},
    claimRequestTypes: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true },
    userCommunicationTypes: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true },
    claimsForPersonByAffectedPeople: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: false },
    claimsForPersonByClaimantClaims: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: false },
    professionals: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: true },
    reportDefinitions: { selectedFilters: [], hideFilter: false, beforeFilters: null, wasEmptyBeforeFilter: true, filterByOnlyActiveRecords: false }
};

const filtersSlice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        setFilterByCurrentUser: (state, action: PayloadAction<{ table: FilterTableKey; filterByCurrentUser: boolean }>) => {
            const { table, filterByCurrentUser } = action.payload;
            state[table].filterByCurrentUser = filterByCurrentUser;
        },
        setFilterByOnlyActiveRecords: (state, action: PayloadAction<{ table: FilterTableKey; filterByOnlyActiveRecords: boolean }>) => {
            const { table, filterByOnlyActiveRecords } = action.payload;
            state[table].filterByOnlyActiveRecords = filterByOnlyActiveRecords;
        },
        setSelectedFilters: (state, action: PayloadAction<{ table: FilterTableKey; filters: SelectedFilter[] }>) => {
            const { table, filters } = action.payload;
            state[table].selectedFilters = filters;
        },
        addFilter: (state, action: PayloadAction<{ table: FilterTableKey; filter: SelectedFilter }>) => {const { table, filter } = action.payload;
            const tableState = state[table];

            const existingFilterIndex = tableState.selectedFilters.findIndex(
                (f) => f.name === filter.name
            );

            if (existingFilterIndex > -1) {
                const existingFilter = tableState.selectedFilters[existingFilterIndex];

                let relatedFilters = existingFilter.relatedFilters ?? [];

                if (filter.relatedFilters) {
                    filter.relatedFilters.forEach((newRelatedFilter) => {
                        const existingRelatedFilterIndex = relatedFilters.findIndex(
                            (relatedFilter) =>
                                relatedFilter.name === newRelatedFilter.name
                        );

                        if (existingRelatedFilterIndex > -1) {
                            relatedFilters[existingRelatedFilterIndex] = newRelatedFilter;
                        } else {
                            relatedFilters.push(newRelatedFilter);
                        }
                    });
                }

                tableState.selectedFilters[existingFilterIndex] = {
                    ...existingFilter,
                    values: filter.values,
                    description: filter.description,
                    relatedFilters
                };
            } else {
                tableState.selectedFilters.push(filter);
            }
        },
        removeFilter: (state, action: PayloadAction<{ table: FilterTableKey; filterName: string }>) => {
            const { table, filterName } = action.payload;
            state[table].selectedFilters = state[table].selectedFilters.filter(
                (f) => f.name !== filterName
            );
        },
        clearFilters: (state, action: PayloadAction<FilterTableKey>) => {
            const table = action.payload;
            state[table].selectedFilters = [];
        },
        clearAllFilters: (state) => {
            (Object.keys(state) as FilterTableKey[]).forEach((table) => {
                state[table].selectedFilters = [];
                state[table].beforeFilters = null;
                state[table].wasEmptyBeforeFilter = true;
            });
        },
        setHideFilter: (state, action: PayloadAction<{ table: FilterTableKey; hide: boolean }>) => {
            const { table, hide } = action.payload;
            state[table].hideFilter = hide;
        },
        saveBeforeFilter: (state, action: PayloadAction<FilterTableKey>) => {
            const table = action.payload;
            const currentFilters = state[table].selectedFilters;

            state[table].beforeFilters = currentFilters.length > 0 ? [...currentFilters] : null;
            state[table].wasEmptyBeforeFilter = currentFilters.length === 0;
        },
        restoreBeforeFilter: (state, action: PayloadAction<FilterTableKey>) => {
            const table = action.payload;
            const tableState = state[table];

            if (tableState.beforeFilters !== null) {
                tableState.selectedFilters = [...tableState.beforeFilters];
                tableState.beforeFilters = null;
                tableState.wasEmptyBeforeFilter = true;
            }
        },
        clearBeforeFilter: (state, action: PayloadAction<FilterTableKey>) => {
            const table = action.payload;
            state[table].beforeFilters = null;
            state[table].wasEmptyBeforeFilter = true;
        }
    },
});

export const {
    setFilterByCurrentUser,
    setFilterByOnlyActiveRecords,
    setSelectedFilters,
    addFilter,
    removeFilter,
    clearFilters,
    clearAllFilters,
    setHideFilter,
    saveBeforeFilter,
    restoreBeforeFilter,
    clearBeforeFilter
} = filtersSlice.actions;

export default filtersSlice.reducer;
